import EventEmitter from 'eventemitter3';
import styles from './styles.module.scss';
import { Dropdown } from '../dropdown';
import { type StockData } from '../../helper/hxKLine';
import * as standardChart from '../../helper/standardChart';
import { type ExtensionApi } from '../../core/extension';
import { deepMerge } from '../../util/data';
import { throttle } from '../../util/common';

const selectedTextColor = 'rgba(0,0,0,0.6)';
const disableTextColor = 'rgba(0,0,0,0.2)';

const LEGEND_AIGC_VIS_KEY = '__aigc-vis-id';
const LEGEND_AIGC_VIS_ATTR = 'toolbarLegendContent';

export enum ShowType {
  Line,
  Rect,
  Round,
  RoundWithLine,
  KLine
}

const showTypeClassMap = {
  [ShowType.Line]: styles.line,
  [ShowType.Rect]: styles.rect,
  [ShowType.Round]: styles.round,
  [ShowType.RoundWithLine]: styles.roundWithLine,
  [ShowType.KLine]: ''
};

type NormalItem = {
  color: string;
  name: string;
  type: ShowType;
  selected: boolean;
  disableSelect?: boolean;
  dom: HTMLElement;
  /** 只用于叙事可视化（AIDP） */
  __blur?: number;
};

type KLineItem = {
  type: ShowType.KLine;
  stocks: StockData[];
  defaultValueIndex: number;
  dom: HTMLElement;
};

type Item = NormalItem | KLineItem;

export type Layout = 'all' | 'scroll' | 'pagination' | 'none';

export type LegendTheme = {
  dvLayout?: Layout;
  data: Item[];
  backgroundColor?: string;
  textStyle?: {
    fontSize?: number;
    lineHeight?: number;
    fontFamily?: string;
    fontWeight?: string;
    color?: string;
    dvDisabledTextColor?: string;
  };
  dvPagination?: {
    color?: string;
    fontSize?: number;
    lineHeight?: number;
    fontFamily?: string;
    fontWeight?: string;
    image?: string;
    imageHover?: string;
    imageDisabled?: string;
  };
  dvIcon?: {
    gap?: number;
    line?: {
      width?: number;
      height?: number;
    };
    rect?: {
      width?: number;
    };
    round?: {
      r?: number;
    };
  };
  dvLegendGap?: number;
};

export type Config = {
  data: Item[];
  layout: Layout;
  token?: ExtensionApi['token'];
};

type EventTypes = {
  legendClick: (event: { data: Item }) => void;
  legendEnter: (event: { data: Item }) => void;
  legendLeave: (event: { data: Item }) => void;
};

type ThemeToken = {
  '--theme-background-color': string | undefined;
  '--theme-font-size': string | undefined | number;
  '--theme-line-height': string | undefined | number;
  '--theme-font-color': string | undefined;
  '--theme-default-color': string | undefined;
  '--theme-icon': string | undefined;
  '--theme-icon-hover': string | undefined;
  '--theme-icon-disabled': string | undefined;
  '--theme-font-weight': string | undefined;
  '--theme-pagination-font-family': string | undefined;
  '--theme-font-family': string | undefined;
  '--theme-pagination-font-size': string | undefined | number;
  '--theme-pagination-line-height': string | undefined | number;
  '--theme-pagination-font-weight': string | undefined | number;
  '--theme-pagination-font-color': string | undefined;
  '--theme-icon-gap': string | undefined | number;
  '--theme-legend-gap': string | undefined | number;
  '--theme-icon-line-width': string | undefined | number;
  '--theme-icon-line-height': string | undefined | number;
  '--theme-icon-rect-width': string | undefined | number;
  '--theme-icon-round-r': string | undefined | number;
};

export class Legend extends EventEmitter<EventTypes> {
  static stringToDom(htmlString: string) {
    const template = document.createElement('template');
    template.innerHTML = htmlString.trim();
    return template.content.firstChild as HTMLElement;
  }

  static createIcon(config: {
    type: NormalItem['type'];
    color: NormalItem['color'];
    selected?: NormalItem['selected'];
  }) {
    return `<div class="${styles.icon} ${Legend.type2Class(config.type)}">
    <i class="${!config.selected ? styles['icon-default'] : undefined}" style="background: ${
      config.selected ? config.color : undefined
    };border-color:${config.selected ? config.color : undefined}"></i>
    </div>`;
  }

  static type2Class(type: ShowType) {
    let className = showTypeClassMap[type];
    if (!className) {
      className = showTypeClassMap[ShowType.Rect];
    }
    return className;
  }

  private __$dom!: HTMLElement;

  private __config: Config;

  private __$pageDom!: HTMLElement;

  private __$contentDom!: HTMLElement;

  private __contentBoxWidth = 0;

  private __userOptions!: LegendTheme;

  private __themeToken!: ThemeToken;

  private curPage = 1;

  private maxPage = 0;

  private nowLeft = 0;

  private maxLeft = 0;

  klineDropdown: Dropdown[] = [];

  private __resizeHandler: () => void;

  constructor(container: HTMLElement, config: Config) {
    super();
    if (config.layout === 'none') {
      return;
    }

    this.__config = config;
    let contentDom = container.querySelector(`[${LEGEND_AIGC_VIS_KEY}="${LEGEND_AIGC_VIS_ATTR}"]`);

    if (contentDom == null) {
      contentDom = document.createElement('div');
      contentDom.setAttribute(LEGEND_AIGC_VIS_KEY, LEGEND_AIGC_VIS_ATTR);
      container.appendChild(contentDom);
    } else {
      contentDom.innerHTML = '';
    }

    this.__$dom = contentDom as HTMLElement;
    this.__$dom.classList.add(styles.wrapper, styles['theme-container']);

    this.__resizeHandler = throttle(this.resize.bind(this), 100);
    window.addEventListener('resize', this.__resizeHandler);

    this.__initialize();
  }

  private __initialize() {
    this.__initUserOptions();
    this.__userOptionToTheme();
    this.__initTheme();
    this.__layout();
  }

  /** 初始化主题 */
  private __initTheme() {
    const themeConfig = this.__themeToken;
    if (!themeConfig) {
      return;
    }

    Object.keys(themeConfig).forEach(key => {
      if (typeof themeConfig[key] !== 'undefined') {
        this.__$dom.style.setProperty(key, themeConfig[key]);
      }
    });
  }

  /** 初始化用户配置 */
  private __initUserOptions() {
    const token = this.__config.token || {};
    const uedTokenToOption = standardChart.uedTokenToOption(token).legend;
    const userOption = token?.dvStandardChart?.baseOption?.legend;
    this.__userOptions = deepMerge(userOption, uedTokenToOption) || {};
  }

  private __userOptionToTheme() {
    const userOption = this.__userOptions;
    this.__themeToken = {
      '--theme-background-color': userOption.backgroundColor,
      '--theme-font-size': userOption.textStyle?.fontSize
        ? `${userOption.textStyle?.fontSize}px`
        : userOption.textStyle?.fontSize,
      '--theme-line-height': userOption.textStyle?.lineHeight
        ? `${userOption.textStyle?.lineHeight}px`
        : userOption.textStyle?.lineHeight,
      '--theme-font-color': userOption.textStyle?.color,
      '--theme-default-color': userOption.textStyle?.dvDisabledTextColor,
      '--theme-icon': userOption.dvPagination?.image,
      '--theme-icon-hover': userOption.dvPagination?.imageHover,
      '--theme-icon-disabled': userOption.dvPagination?.imageDisabled,
      '--theme-font-weight': userOption.textStyle?.fontWeight,
      '--theme-font-family': userOption.textStyle?.fontFamily,
      '--theme-pagination-font-family': userOption.dvPagination?.fontFamily,
      '--theme-pagination-font-size': userOption.dvPagination?.fontSize
        ? `${userOption.dvPagination?.fontSize}px`
        : userOption.dvPagination?.fontSize,
      '--theme-pagination-line-height': userOption.dvPagination?.lineHeight
        ? `${userOption.dvPagination?.lineHeight}px`
        : userOption.dvPagination?.lineHeight,
      '--theme-pagination-font-weight': userOption.dvPagination?.fontWeight,
      '--theme-pagination-font-color': userOption.dvPagination?.color,
      '--theme-icon-gap': userOption.dvIcon?.gap
        ? `${userOption.dvIcon?.gap}px`
        : userOption.dvIcon?.gap,
      '--theme-legend-gap': userOption.dvLegendGap
        ? `${userOption.dvLegendGap}px`
        : userOption.dvLegendGap,
      '--theme-icon-line-width': userOption.dvIcon?.line?.width
        ? `${userOption.dvIcon?.line?.width}px`
        : userOption.dvIcon?.line?.width,
      '--theme-icon-line-height': userOption.dvIcon?.line?.height
        ? `${userOption.dvIcon?.line?.height}px`
        : userOption.dvIcon?.line?.height,
      '--theme-icon-rect-width': userOption.dvIcon?.rect?.width
        ? `${userOption.dvIcon?.rect?.width}px`
        : userOption.dvIcon?.rect?.width,
      '--theme-icon-round-r': userOption.dvIcon?.round?.r
        ? `${userOption.dvIcon?.round?.r}px`
        : userOption.dvIcon?.round?.r
    };
  }

  update(data: Item[]) {
    this.removeAllListeners();
    this.curPage = 1;
    this.__$dom.innerHTML = '';
    this.__config.data = data;
    this.__layout();
  }

  private __layout() {
    const { layout } = this.__config;
    if (this.__userOptions.backgroundColor) {
      this.__$dom.style.backgroundColor = this.__userOptions.backgroundColor;
    }
    if (layout === 'all') {
      this.__layoutAll();
    }
    if (layout === 'pagination') {
      this.__layoutPagination();
    }
    if (layout === 'scroll') {
      this.__layoutScroll();
    }
  }

  private __layoutAll() {
    const $allContainer = document.createElement('div');
    $allContainer.classList.add(styles['all-container']);

    this.__config.data.forEach((item, index) => {
      let itemDom: HTMLElement;
      if (item.type === ShowType.KLine) {
        itemDom = this.__buildKLine(item as KLineItem);
        $allContainer.appendChild(itemDom);
      } else {
        itemDom = this.__buildLegendDom(item);
        $allContainer.appendChild(itemDom);
      }
      this.__config.data[index].dom = itemDom;
    });

    this.__$dom.appendChild($allContainer);
  }

  private __layoutPagination() {
    // 清理旧的内容和分页器
    if (this.__$contentDom && this.__$contentDom.parentNode) {
      this.__$contentDom.parentNode.removeChild(this.__$contentDom);
    }
    if (this.__$pageDom && this.__$pageDom.parentNode) {
      this.__$pageDom.parentNode.removeChild(this.__$pageDom);
    }
    this.__$contentDom = document.createElement('div');
    this.__$contentDom.classList.add(styles['content-container']);

    this.__config.data.forEach((item, index) => {
      let itemDom: HTMLElement;
      if (item.type === ShowType.KLine) {
        itemDom = this.__buildKLine(item as KLineItem);
        this.__$contentDom.appendChild(itemDom);
      } else {
        itemDom = this.__buildLegendDom(item);
        this.__$contentDom.appendChild(itemDom);
      }
      this.__config.data[index].dom = itemDom;
    });

    this.__$pageDom = Legend.stringToDom(`
      <div class="${styles.pageContainer}">
        <div class="${styles.prev}"></div>
        <div class="${styles.page}"></div>
        <div class="${styles.next}"></div>
      </div>
    `);

    this.__$dom.appendChild(this.__$contentDom);
    this.__$dom.appendChild(this.__$pageDom);

    this.__updatePageDom();
    this.__bindEvents();
  }

  private __layoutScroll() {
    const $scrollContainer = document.createElement('div');
    $scrollContainer.classList.add(styles['scroll-container']);

    this.__config.data.forEach((item, index) => {
      let itemDom: HTMLElement;
      if (item.type === ShowType.KLine) {
        itemDom = this.__buildKLine(item as KLineItem);
        $scrollContainer.appendChild(itemDom);
      } else {
        itemDom = this.__buildLegendDom(item);
        $scrollContainer.appendChild(itemDom);
      }
      this.__config.data[index].dom = itemDom;
    });

    this.__$dom.appendChild($scrollContainer);
  }

  private __buildKLine(item: KLineItem) {
    const itemDom = document.createElement('div') as HTMLElement;
    const data = item.stocks.map(stock => ({
      key: String(stock.code),
      text: stock.name
    }));
    const dropdown = new Dropdown(itemDom, {
      theme: 'kline-legend',
      data,
      defaultValue: data[item.defaultValueIndex].key,
      listAutoHide: true,
      listPositionFixed: true,
      token: this.__config.token?.dvDropdown
    });

    this.klineDropdown.push(dropdown);

    return itemDom;
  }

  private __buildLegendDom(config: NormalItem) {
    const $itemContainer = document.createElement('div');
    $itemContainer.classList.add(styles['item-container']);
    $itemContainer.style.cursor = config.disableSelect ? 'default' : 'pointer';
    $itemContainer.style.opacity = Number.isNaN(config.__blur) ? 1 : config.__blur;

    const $icon = Legend.stringToDom(
      Legend.createIcon({
        type: config.type,
        color: config.color,
        selected: config.selected
      })
    );

    const $itemName = Legend.stringToDom(
      `<span class="${!config.selected ? styles['icon-text'] : undefined} ${styles.text}">${
        config.name
      }</span>`
    );

    $itemContainer.appendChild($icon);
    $itemContainer.appendChild($itemName);

    $itemContainer.addEventListener('click', () => {
      if (!config.disableSelect) {
        config.selected = !config.selected;
        this.emit('legendClick', { data: config });
        this.__updateLegendDom(config);
      }
    });

    $itemContainer.addEventListener('mouseenter', e => {
      if (e.target === $itemContainer) {
        this.emit('legendEnter', { data: config });
      }
    });

    $itemContainer.addEventListener('mouseleave', e => {
      if (e.target === $itemContainer) {
        this.emit('legendLeave', { data: config });
      }
    });

    return $itemContainer;
  }

  private __updateLegendDom(config: NormalItem) {
    const { textStyle } = this.__userOptions;
    const icon = config.dom?.querySelector('i');
    const text = config.dom?.querySelector('span');
    if (config.selected) {
      icon!.style.background = config.color;
      icon!.style.borderColor = config.color;
      text!.style.color = textStyle?.color || selectedTextColor;
    } else {
      icon!.style.background = textStyle?.dvDisabledTextColor || disableTextColor;
      icon!.style.borderColor = textStyle?.dvDisabledTextColor || disableTextColor;
      text!.style.color = textStyle?.dvDisabledTextColor || disableTextColor;
    }
  }

  private __updatePageDom() {
    const scrollW = this.__$contentDom.scrollWidth;
    this.__contentBoxWidth = this.__$contentDom.offsetWidth;

    // 有遮挡 出现page
    if (scrollW > this.__contentBoxWidth) {
      this.maxLeft = scrollW - this.__contentBoxWidth;
      this.maxPage = Math.ceil(scrollW / this.__contentBoxWidth);
      this.setPage();
      this.__$pageDom.style.visibility = 'visible';
      this.__$pageDom.style.pointerEvents = 'initial';
    }
  }

  private __bindEvents() {
    const prev = this.__$pageDom.querySelector(`.${styles.prev}`);
    const next = this.__$pageDom.querySelector(`.${styles.next}`);
    prev?.addEventListener('click', () => {
      this.prevFunc();
    });
    next?.addEventListener('click', () => {
      this.nextFunc();
    });
  }

  private prevFunc() {
    if (this.curPage === 1) {
      return;
    }
    if (this.curPage === 2) {
      this.__$contentDom.scrollLeft = 0;
      this.curPage--;
      this.nowLeft = 0;
      this.setPage();
      return;
    }
    const tempLeft = this.nowLeft - this.__contentBoxWidth;
    this.nowLeft = tempLeft < 0 ? 0 : tempLeft;
    this.curPage--;
    this.setPage();
    this.__$contentDom.scrollTo(this.nowLeft, 0);
  }

  private nextFunc() {
    if (this.curPage === this.maxPage) {
      return;
    }
    if (this.curPage === this.maxPage - 1) {
      this.__$contentDom.scrollLeft =
        this.__$contentDom.scrollWidth - this.__$contentDom.offsetWidth;
      this.curPage++;
      this.nowLeft = this.__$contentDom.scrollLeft;
      this.setPage();
      return;
    }
    this.curPage++;
    const tempLeft = this.nowLeft + this.__contentBoxWidth;
    this.nowLeft = tempLeft > this.maxLeft ? this.maxLeft : tempLeft;
    this.setPage();
    this.__$contentDom.scrollTo(this.nowLeft, 0);
  }

  private setPage() {
    const pageDiv = this.__$pageDom.querySelector(`.${styles.page}`);
    const prev = this.__$pageDom.querySelector(`.${styles.prev}`);
    const next = this.__$pageDom.querySelector(`.${styles.next}`);
    if (this.curPage === this.maxPage) {
      (next! as HTMLElement).classList.add(styles['next-disabled']);
    } else {
      (next! as HTMLElement).classList.remove(styles['next-disabled']);
    }
    if (this.curPage === 1) {
      (prev! as HTMLElement).classList.add(styles['prev-disabled']);
    } else {
      (prev! as HTMLElement).classList.remove(styles['prev-disabled']);
    }
    if (pageDiv) {
      pageDiv.innerHTML = `${this.curPage}/${this.maxPage}`;
      const scrollW = this.__$contentDom.scrollWidth;
      this.__contentBoxWidth = this.__$contentDom.offsetWidth;
      this.maxLeft = scrollW - this.__contentBoxWidth;
    }
  }

  /**
   * 外部可主动调用的resize方法
   */
  public resize() {
    if (this.__config.layout === 'pagination') {
      // 记录当前页第一个item的name
      const firstItem = this.__getCurrentPageFirstItem();
      this.__layout(); // 重新布局
      if (firstItem) {
        this.__jumpToItemPage(firstItem.name);
      }
    } else {
      this.__layout();
    }
  }

  /**
   * 获取当前分页第一个item
   */
  private __getCurrentPageFirstItem(): NormalItem | undefined {
    if (!this.__$contentDom) {
      return undefined;
    }
    return this.__config.data.find(
      item => 'name' in item && item.dom && this.__isElementInView(item.dom, this.__$contentDom)
    ) as NormalItem | undefined;
  }

  /**
   * 判断元素是否在容器可视区域
   * @param el - 目标元素
   * @param container - 容器元素
   * @returns 是否在可视区域
   */
  // eslint-disable-next-line class-methods-use-this
  private __isElementInView(el: HTMLElement, container: HTMLElement): boolean {
    const elRect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    return elRect.left >= containerRect.left && elRect.right <= containerRect.right;
  }

  /**
   * 跳转到包含指定name的item所在页
   */
  private __jumpToItemPage(name: string) {
    if (!this.__$contentDom) {
      return;
    }
    for (let i = 0; i < this.__config.data.length; i++) {
      const item = this.__config.data[i];
      if ('name' in item && item.name === name && item.dom) {
        const itemLeft = item.dom.offsetLeft;
        const pageWidth = this.__$contentDom.offsetWidth;
        const page = Math.floor(itemLeft / pageWidth) + 1;
        this.curPage = page;
        this.nowLeft = (page - 1) * pageWidth;
        this.setPage();
        this.__$contentDom.scrollTo(this.nowLeft, 0);
        break;
      }
    }
  }

  destroy() {
    window.removeEventListener('resize', this.__resizeHandler);
    this.klineDropdown.forEach(comp => comp?.destroy());
    if (this.__$dom?.innerHTML) {
      this.__$dom.innerHTML = '';
    }
    this.__$dom?.remove();
  }
}
